import React, { useState } from "react";
import { PRIORITIES, BULK_MAX_IDS } from "./supportEnums";

/** Actions offered in the bar, and which extra input each one needs. */
const ACTIONS = [
  { key: "assign", label: "Assign to…", field: "agent" },
  { key: "unassign", label: "Return to pool", field: null },
  { key: "priority", label: "Set priority…", field: "priority" },
  { key: "snooze", label: "Snooze until…", field: "until" },
  { key: "tag", label: "Add tags…", field: "tags" },
  { key: "resolve", label: "Resolve", field: null },
];

/** `datetime-local` needs a local-time string, not an ISO/UTC one. */
const defaultUntil = () => {
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
  tomorrow.setHours(9, 0, 0, 0);
  return new Date(tomorrow.getTime() - tomorrow.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

/**
 * Sticky bar for acting on a multi-row selection.
 *
 * Appears only when rows are ticked. The backend caps a call at
 * {@link BULK_MAX_IDS} ids and reports per-row results, so the caller must
 * surface partial success rather than treating a 200 as "all done".
 *
 * @param {Object} props
 * @param {number} props.selectedCount
 * @param {Array<{ userId: string, name: string }>} props.agents - Assign targets.
 * @param {boolean} [props.busy]
 * @param {(payload: Object) => void} props.onApply - Receives `{ action }` plus
 *   the matching field (`agent_user_id` / `priority` / `until` / `tags`).
 * @param {() => void} props.onClear
 */
export function BulkActionBar({ selectedCount, agents, busy = false, onApply, onClear }) {
  const [action, setAction] = useState("assign");
  const [agentUserId, setAgentUserId] = useState("");
  const [priority, setPriority] = useState("normal");
  const [until, setUntil] = useState(defaultUntil);
  const [tags, setTags] = useState("");

  if (selectedCount === 0) return null;

  const field = ACTIONS.find((a) => a.key === action)?.field ?? null;
  const overLimit = selectedCount > BULK_MAX_IDS;

  const buildPayload = () => {
    switch (field) {
      case "agent":
        return agentUserId ? { action, agent_user_id: agentUserId } : null;
      case "priority":
        return { action, priority };
      case "until":
        return until ? { action, until: new Date(until).toISOString() } : null;
      case "tags": {
        const list = tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
        return list.length ? { action, tags: list } : null;
      }
      default:
        return { action };
    }
  };

  const handleApply = () => {
    const payload = buildPayload();
    if (payload) onApply(payload);
  };

  const controlClass =
    "text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-primary/40";

  return (
    <div className="sticky bottom-3 z-20 flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-3 shadow-lg">
      <span className="text-xs font-semibold text-gray-800">
        {selectedCount} selected
      </span>

      <select
        value={action}
        onChange={(e) => setAction(e.target.value)}
        aria-label="Bulk action"
        className={controlClass}
      >
        {ACTIONS.map((a) => (
          <option key={a.key} value={a.key}>
            {a.label}
          </option>
        ))}
      </select>

      {field === "agent" && (
        <select
          value={agentUserId}
          onChange={(e) => setAgentUserId(e.target.value)}
          aria-label="Assign to"
          className={controlClass}
        >
          <option value="">Pick an agent</option>
          {agents.map((agent) => (
            <option key={agent.userId} value={agent.userId}>
              {agent.name}
            </option>
          ))}
        </select>
      )}

      {field === "priority" && (
        <select
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          aria-label="Priority"
          className={controlClass}
        >
          {PRIORITIES.map((p) => (
            <option key={p.key} value={p.key}>
              {p.label}
            </option>
          ))}
        </select>
      )}

      {field === "until" && (
        <input
          type="datetime-local"
          value={until}
          onChange={(e) => setUntil(e.target.value)}
          aria-label="Snooze until"
          className={controlClass}
        />
      )}

      {field === "tags" && (
        <input
          type="text"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="refund, payment-failed"
          aria-label="Tags, comma separated"
          className={controlClass}
        />
      )}

      {overLimit && (
        <span className="text-xs font-semibold text-danger-dark">
          Select at most {BULK_MAX_IDS} tickets
        </span>
      )}

      <button
        type="button"
        onClick={handleApply}
        disabled={busy || overLimit}
        className="ml-auto rounded-lg bg-orange-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? "Applying…" : "Apply"}
      </button>
      <button
        type="button"
        onClick={onClear}
        className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
      >
        Clear
      </button>
    </div>
  );
}

export default BulkActionBar;
